import React, { createContext, useContext, useEffect, useState } from 'react'

// Theme variants available in the system
export type ThemeVariant = 'cp2077' | 'nebula-orange' | 'light-wispy'

// Theme mode (light/dark preference)
export type ThemeMode = 'light' | 'dark' | 'system'

// Theme variant metadata for UI
export const themeVariants: Record<ThemeVariant, {
  name: string
  description: string
  primaryColor: string
  secondaryColor?: string
  isDark: boolean
  defaultBgImage: string | null
}> = {
  'cp2077': {
    name: 'CP Admin 2077',
    description: 'Sharp edges, gradient cards, cyan accents',
    primaryColor: '#FF6B35',
    secondaryColor: '#00F0FF',
    isDark: true,
    defaultBgImage: null, // No background image by default
  },
  'nebula-orange': {
    name: 'Nebula Glass',
    description: 'Glassmorphism with orange glow',
    primaryColor: '#FF6B35',
    isDark: true,
    defaultBgImage: '/images/nebula-bg.png',
  },
  'light-wispy': {
    name: 'Light & Wispy',
    description: 'Frosted glass on white, purple accents',
    primaryColor: '#8B5CF6',
    isDark: false,
    defaultBgImage: '/images/clouds-bg.png',
  },
}

// Custom background images per theme (stored in localStorage)
export type ThemeBackgrounds = Record<ThemeVariant, string | null>

interface ThemeContextType {
  // Legacy light/dark mode (kept for backwards compatibility)
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
  resolvedMode: 'light' | 'dark'

  // Theme variant system
  variant: ThemeVariant
  setVariant: (variant: ThemeVariant) => void

  // Background image toggle
  showBackgroundImage: boolean
  setShowBackgroundImage: (show: boolean) => void

  // Custom background images per theme
  customBackgrounds: ThemeBackgrounds
  setCustomBackground: (theme: ThemeVariant, imageUrl: string | null) => void
  getCurrentBackgroundImage: () => string | null
  resetBackgroundToDefault: (theme: ThemeVariant) => void

  // Legacy aliases (for backwards compatibility)
  theme: ThemeMode
  setTheme: (mode: ThemeMode) => void
  resolvedTheme: 'light' | 'dark'
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Default backgrounds (null means use CSS variable default)
const getDefaultBackgrounds = (): ThemeBackgrounds => ({
  'cp2077': null,
  'nebula-orange': null,
  'light-wispy': null,
})

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>('dark')
  const [resolvedMode, setResolvedMode] = useState<'light' | 'dark'>('dark')
  const [variant, setVariantState] = useState<ThemeVariant>('nebula-orange')
  const [showBackgroundImage, setShowBackgroundImageState] = useState(true)
  const [customBackgrounds, setCustomBackgroundsState] = useState<ThemeBackgrounds>(getDefaultBackgrounds)

  // Load saved preferences on mount
  useEffect(() => {
    const savedMode = localStorage.getItem('theme-mode') as ThemeMode | null
    const savedVariant = localStorage.getItem('theme-variant') as ThemeVariant | null
    const savedBgImage = localStorage.getItem('theme-bg-image')
    const savedCustomBgs = localStorage.getItem('theme-custom-backgrounds')

    // Also check legacy 'theme' key for backwards compatibility
    const legacyTheme = localStorage.getItem('theme') as ThemeMode | null

    if (savedMode) {
      setModeState(savedMode)
    } else if (legacyTheme) {
      setModeState(legacyTheme)
    }

    if (savedVariant && savedVariant in themeVariants) {
      setVariantState(savedVariant)
    }

    if (savedBgImage !== null) {
      setShowBackgroundImageState(savedBgImage === 'true')
    }

    if (savedCustomBgs) {
      try {
        const parsed = JSON.parse(savedCustomBgs) as ThemeBackgrounds
        setCustomBackgroundsState(prev => ({ ...prev, ...parsed }))
      } catch {
        // Invalid JSON, use defaults
      }
    }
  }, [])

  // Resolve mode based on system preference if needed
  useEffect(() => {
    const updateResolvedMode = () => {
      if (mode === 'system') {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
        setResolvedMode(systemTheme)
      } else {
        setResolvedMode(mode)
      }
    }

    updateResolvedMode()

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      if (mode === 'system') {
        updateResolvedMode()
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [mode])

  // Apply theme variant to document
  useEffect(() => {
    const root = document.documentElement

    // Set the theme variant data attribute
    root.setAttribute('data-theme', variant)

    // Set light/dark class based on theme variant's default
    const variantInfo = themeVariants[variant]
    root.classList.remove('light', 'dark')
    root.classList.add(variantInfo.isDark ? 'dark' : 'light')

    // Add smooth transition class temporarily
    root.classList.add('theme-transition')
    setTimeout(() => {
      root.classList.remove('theme-transition')
    }, 300)
  }, [variant])

  // Apply background image toggle
  useEffect(() => {
    const root = document.documentElement
    if (showBackgroundImage) {
      root.removeAttribute('data-bg-image')
    } else {
      root.setAttribute('data-bg-image', 'off')
    }
  }, [showBackgroundImage])

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode)
    localStorage.setItem('theme-mode', newMode)
    localStorage.setItem('theme', newMode) // Also set legacy key
  }

  const setVariant = (newVariant: ThemeVariant) => {
    setVariantState(newVariant)
    localStorage.setItem('theme-variant', newVariant)
  }

  const setShowBackgroundImage = (show: boolean) => {
    setShowBackgroundImageState(show)
    localStorage.setItem('theme-bg-image', String(show))
  }

  // Set custom background for a specific theme
  const setCustomBackground = (theme: ThemeVariant, imageUrl: string | null) => {
    const newBackgrounds = { ...customBackgrounds, [theme]: imageUrl }
    setCustomBackgroundsState(newBackgrounds)
    localStorage.setItem('theme-custom-backgrounds', JSON.stringify(newBackgrounds))
  }

  // Get current background image URL for active theme
  const getCurrentBackgroundImage = (): string | null => {
    // Custom background takes priority
    const customBg = customBackgrounds[variant]
    if (customBg) return customBg

    // Fall back to theme default
    return themeVariants[variant].defaultBgImage
  }

  // Reset a theme's background to its default
  const resetBackgroundToDefault = (theme: ThemeVariant) => {
    setCustomBackground(theme, null)
  }

  return (
    <ThemeContext.Provider
      value={{
        mode,
        setMode,
        resolvedMode,
        variant,
        setVariant,
        showBackgroundImage,
        setShowBackgroundImage,
        customBackgrounds,
        setCustomBackground,
        getCurrentBackgroundImage,
        resetBackgroundToDefault,
        // Legacy aliases
        theme: mode,
        setTheme: setMode,
        resolvedTheme: resolvedMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
